"use client";

import {
  createContext,
  isValidElement,
  useContext,
  type ReactElement,
} from "react";
import Image from "next/image";
import { cva, type VariantProps } from "class-variance-authority";
import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import { Drawer as DrawerPrimitive } from "vaul";
import { cn } from "@/lib/utils";
import useIsMobile from "@/hooks/use-is-mobile";
import { Icon, type IconName } from "@/components/ui/icon";
import { SkeletonBox, SkeletonContainer } from "@/components/ui/skeleton";

const modalContentVariants = cva(
  "fixed inset-x-4 top-1/2 z-50 mx-auto flex w-auto -translate-y-1/2 flex-col overflow-hidden max-h-[92dvh] rounded-xl border border-border bg-surface-elevated shadow-[var(--modal-shadow)] outline-none duration-200 ease-out data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-[0.97] data-open:animate-in data-open:fade-in-0 data-open:zoom-in-[0.97] sm:left-1/2 sm:right-auto sm:w-full sm:-translate-x-1/2",
  {
    variants: {
      size: {
        sm: "sm:max-w-sm",
        md: "sm:max-w-lg",
        lg: "sm:max-w-3xl",
        xl: "sm:max-w-4xl",
        "2xl": "sm:max-w-5xl",
      },
    },
    defaultVariants: {
      size: "md",
    },
  },
);

const ModalMobileContext = createContext<boolean | null>(null);

function useModalMobile() {
  const contextValue = useContext(ModalMobileContext);
  const isMobile = useIsMobile();
  return contextValue ?? isMobile;
}

export interface ModalProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  dismissible?: boolean;
  children?: React.ReactNode;
}

export function Modal({
  open,
  defaultOpen,
  onOpenChange,
  dismissible = true,
  children,
}: ModalProps) {
  const isMobile = useIsMobile();
  const content = (
    <ModalMobileContext.Provider value={isMobile}>
      {children}
    </ModalMobileContext.Provider>
  );

  if (isMobile) {
    return (
      <DrawerPrimitive.Root
        open={open}
        defaultOpen={defaultOpen}
        dismissible={dismissible}
        onOpenChange={(next) => onOpenChange?.(next)}
      >
        {content}
      </DrawerPrimitive.Root>
    );
  }
  return (
    <DialogPrimitive.Root
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={(next, details) => {
        if (!next) {
          if (details.reason === "outside-press") {
            const target = details.event.target as HTMLElement | null;
            if (target?.closest("[data-portal-dropdown]")) return;
            if (!dismissible) return;
          }
          if (details.reason === "escape-key" && !dismissible) return;
        }
        onOpenChange?.(next);
      }}
    >
      {content}
    </DialogPrimitive.Root>
  );
}

type ModalTriggerProps = (
  | DialogPrimitive.Trigger.Props
  | React.ComponentProps<typeof DrawerPrimitive.Trigger>
) & {
  asChild?: boolean;
};

export function ModalTrigger({
  asChild,
  children,
  ...props
}: ModalTriggerProps) {
  const isMobile = useModalMobile();

  if (isMobile) {
    return (
      <DrawerPrimitive.Trigger
        data-slot="modal-trigger"
        asChild={asChild}
        {...(props as React.ComponentProps<typeof DrawerPrimitive.Trigger>)}
      >
        {children}
      </DrawerPrimitive.Trigger>
    );
  }

  if (asChild && isValidElement(children)) {
    return (
      <DialogPrimitive.Trigger
        data-slot="modal-trigger"
        render={children as ReactElement}
        {...props}
      />
    );
  }
  return (
    <DialogPrimitive.Trigger data-slot="modal-trigger" {...props}>
      {children}
    </DialogPrimitive.Trigger>
  );
}

export function ModalPortal(props: DialogPrimitive.Portal.Props) {
  return <DialogPrimitive.Portal data-slot="modal-portal" {...props} />;
}

export function ModalOverlay({
  className,
  ...props
}: DialogPrimitive.Backdrop.Props) {
  return (
    <DialogPrimitive.Backdrop
      data-slot="modal-overlay"
      // Force the backdrop to render even when this Dialog is nested inside
      // another open Dialog (e.g. an action modal over the profile <Sheet>).
      // base-ui drops a nested dialog's backdrop by default
      // (enabled: forceRender || !nested), which would strip our scrim + blur.
      forceRender
      className={cn(
        "fixed inset-0 z-50 bg-black/60 duration-200 supports-backdrop-filter:backdrop-blur-xs data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0",
        className,
      )}
      {...props}
    />
  );
}

type ModalCloseProps = (
  | DialogPrimitive.Close.Props
  | React.ComponentProps<typeof DrawerPrimitive.Close>
) & {
  asChild?: boolean;
};

export function ModalClose({ asChild, children, ...props }: ModalCloseProps) {
  const isMobile = useModalMobile();

  if (isMobile) {
    return (
      <DrawerPrimitive.Close
        data-slot="modal-close"
        asChild={asChild}
        {...(props as React.ComponentProps<typeof DrawerPrimitive.Close>)}
      >
        {children}
      </DrawerPrimitive.Close>
    );
  }

  if (asChild && isValidElement(children)) {
    return (
      <DialogPrimitive.Close
        data-slot="modal-close"
        render={children as ReactElement}
        {...(props as DialogPrimitive.Close.Props)}
      />
    );
  }

  return (
    <DialogPrimitive.Close
      data-slot="modal-close"
      {...(props as DialogPrimitive.Close.Props)}
    >
      {children}
    </DialogPrimitive.Close>
  );
}

export interface ModalContentProps
  extends
    DialogPrimitive.Popup.Props,
    VariantProps<typeof modalContentVariants> {}

export function ModalContent({
  className,
  children,
  size,
  ...props
}: ModalContentProps) {
  const isMobile = useModalMobile();

  if (isMobile) {
    return (
      <DrawerPrimitive.Portal>
        <DrawerPrimitive.Overlay className="fixed inset-0 z-50 bg-black/60" />
        <DrawerPrimitive.Content
          className={cn(
            "fixed inset-x-0 overflow-hidden bottom-0 z-50 flex flex-col rounded-t-xl border border-border border-b-0 bg-surface-elevated focus:outline-none max-h-[92dvh]",
            className,
          )}
        >
          <DrawerPrimitive.Title className="sr-only">
            Dialog
          </DrawerPrimitive.Title>
          {children}
        </DrawerPrimitive.Content>
      </DrawerPrimitive.Portal>
    );
  }

  return (
    <ModalPortal>
      <ModalOverlay />
      <DialogPrimitive.Popup
        data-slot="modal-content"
        className={cn(modalContentVariants({ size }), className)}
        initialFocus={false}
        {...props}
      >
        {children}
      </DialogPrimitive.Popup>
    </ModalPortal>
  );
}

export function ModalTitle({
  className,
  ...props
}: DialogPrimitive.Title.Props) {
  const isMobile = useModalMobile();

  if (isMobile) {
    return (
      <DrawerPrimitive.Title
        data-slot="modal-title"
        className={cn("text-sm font-medium text-heading", className)}
        {...(props as React.ComponentProps<typeof DrawerPrimitive.Title>)}
      />
    );
  }

  return (
    <DialogPrimitive.Title
      data-slot="modal-title"
      className={cn("text-[1rem] font-medium text-heading", className)}
      {...props}
    />
  );
}

export function ModalDescription({
  className,
  ...props
}: DialogPrimitive.Description.Props) {
  const isMobile = useModalMobile();

  if (isMobile) {
    return (
      <DrawerPrimitive.Description
        data-slot="modal-description"
        className={cn("text-sm text-contrast/60 leading-normal", className)}
        {...(props as React.ComponentProps<typeof DrawerPrimitive.Description>)}
      />
    );
  }

  return (
    <DialogPrimitive.Description
      data-slot="modal-description"
      className={cn("text-sm text-contrast/60 leading-normal", className)}
      {...props}
    />
  );
}

export interface ModalHeaderProps extends React.ComponentProps<"div"> {
  title: string;
  subtitle?: React.ReactNode;
  icon?: string;
  iconComponent?: React.ComponentType<{
    width?: number;
    height?: number;
    className?: string;
  }>;
  iconName?: IconName;
  showCloseButton?: boolean;
}

export function ModalHeader({
  title,
  subtitle,
  icon,
  iconComponent: IconComponent,
  iconName,
  showCloseButton = true,
  className,
  ...props
}: ModalHeaderProps) {
  return (
    <div
      className={cn(
        "bg-surface-header border-b border-border h-10 px-5 flex items-center justify-between shrink-0",
        className,
      )}
      {...props}
    >
      <div className="flex items-center gap-1.5">
        {icon && (
          <Image
            src={icon}
            alt=""
            width={16}
            height={16}
            className="size-4 opacity-70 brightness-0 dark:brightness-100"
          />
        )}
        {IconComponent && (
          <IconComponent
            width={16}
            height={16}
            className="size-4 text-contrast opacity-70"
          />
        )}
        {iconName && (
          <Icon name={iconName} size={16} className="text-heading opacity-70" />
        )}
        <ModalTitle>{title}</ModalTitle>
        {subtitle && (
          <span className="text-sm font-normal text-contrast/50 leading-5">
            {subtitle}
          </span>
        )}
      </div>
      {showCloseButton && (
        <ModalClose asChild>
          <button
            type="button"
            className="flex items-center justify-center transition-colors cursor-pointer text-contrast/40 hover:text-contrast/70"
          >
            <Icon name="IconCrossMedium" size={14} />
          </button>
        </ModalClose>
      )}
    </div>
  );
}

export type ModalBodyProps = React.ComponentProps<"div">;

export function ModalBody({ className, ...props }: ModalBodyProps) {
  return (
    <div
      className={cn("p-6 flex-1 min-h-0 overflow-y-auto", className)}
      {...props}
    />
  );
}

export interface ModalFooterProps extends React.ComponentProps<"div"> {
  align?: "end" | "between";
}

export function ModalFooter({
  children,
  className,
  align = "end",
  ...props
}: ModalFooterProps) {
  return (
    <div
      className={cn(
        "bg-surface-header border-t border-border px-6 py-3 flex flex-wrap items-center gap-3 shrink-0",
        align === "between" ? "justify-between" : "justify-end",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export function SuspenseModalBody() {
  return (
    <>
      <div className="bg-surface-header border-b border-border h-10 px-5 flex items-center shrink-0">
        <SkeletonContainer>
          <SkeletonBox className="h-3 w-32" />
        </SkeletonContainer>
      </div>
      <div className="p-6 flex-1 min-h-0">
        <SkeletonContainer className="flex-col gap-3">
          <SkeletonBox className="h-3 w-3/4" />
          <SkeletonBox className="h-3 w-2/3" />
          <SkeletonBox className="h-3 w-1/2" />
        </SkeletonContainer>
      </div>
      <div className="bg-surface-header border-t border-border px-6 py-3 flex items-center justify-end gap-3 shrink-0">
        <SkeletonContainer className="gap-3">
          <SkeletonBox className="h-7 w-16" />
          <SkeletonBox className="h-7 w-20" />
        </SkeletonContainer>
      </div>
    </>
  );
}
